import moment from "moment"

export const DATE_FORMAT = "YYYY-MM-DD"
export const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm"
export const DATE_FORMAT_DD = "DD-MM-YYYY"
export const DATE_TIME_FORMAT_DD = "DD/MM/YYYY HH:mm"

export const TIME_FORMAT_SLASH = "HH:mm"
export const DATE_FORMAT_SLASH = "DD/MM/YYYY"
export const DATE_FORMAT_SLASH_Y = "YYYY/MM/DD"

export const REGEX_DATE_FORMAT =
  /^\s*(3[01]|[12][0-9]|0?[1-9])\/(1[012]|0?[1-9])\/((?:19|20)\d{2})\s*$/

export const FULL_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ"

// ================= COMMON =================
const now = () => moment()
const m = (d) => moment(d)

// ================= BASIC =================
export function momentDateToString(date, format = DATE_FORMAT) {
  if (!date) return null
  return m(date).format(format)
}

export function formatDate(date, format = DATE_FORMAT_SLASH) {
  if (!date) return null
  return m(date).format(format)
}

export function formatTime(date, format = TIME_FORMAT_SLASH) {
  if (!date) return null
  return m(date).format(format)
}

// ⚠️ giữ nguyên behavior
export function formatDateAndTime(date, format = DATE_FORMAT) {
  if (!date) return null
  return m(date).format(DATE_TIME_FORMAT_DD)
}

// ================= TIME =================
export function extractTime(endTime, startTime = null) {
  const start = startTime ? m(startTime) : now()
  const end = m(endTime)

  let timeLeft = end.diff(start)
  if (timeLeft < 0) timeLeft = 0

  const d = moment.duration(timeLeft)

  return {
    timeLeft,
    years: Math.trunc(d.asYears()),
    months: Math.trunc(d.asMonths()),
    weeks: Math.trunc(d.asWeeks()),
    days: Math.trunc(d.asDays()),
    hours: Math.trunc(d.asHours()),
    minutes: d.minutes(),
    seconds: d.seconds(),
  }
}

// ================= DAY =================
export function getDayNumberByDate(dt = "now") {
  if (!dt) return null
  const arr = [2, 3, 4, 5, 6, 7, 8]
  const time = dt === "now" ? now() : m(dt)
  return arr[time.format("d")]
}

export function getDayNameByDate(dt = "now") {
  if (!dt) return null
  const arr = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"]
  const time = dt === "now" ? now() : m(dt)
  return arr[time.format("d")]
}

// ================= INTERNAL =================
const parseWeek = (dt, diffDay) => {
  const dateNumber = getDayNumberByDate(dt)
  const x = diffDay % 7
  const y = Math.floor(diffDay / 7)
  const compareDate = x - (9 - dateNumber)
  return compareDate > 0 ? y + 1 : y
}

const diffDateTime = (dt) => {
  const cur = now()

  const get = (fmt, val) =>
    parseInt(momentDateToString(val, fmt))

  const diffSecond = get("YYYYMMDDHHmmss", cur) - get("YYYYMMDDHHmmss", dt)
  const diffMinute = get("YYYYMMDDHHmm", cur) - get("YYYYMMDDHHmm", dt)
  const diffHour = get("YYYYMMDDHH", cur) - get("YYYYMMDDHH", dt)
  const diffDay = get("YYYYMMDD", cur) - get("YYYYMMDD", dt)
  const diffMonth = get("YYYYMM", cur) - get("YYYYMM", dt)
  const diffYear = get("YYYY", cur) - get("YYYY", dt)

  const diffWeek = parseWeek(dt, diffDay)

  return {
    diffSecond,
    diffMinute,
    diffHour,
    diffDay,
    diffWeek,
    diffMonth,
    diffYear,
  }
}

// ================= FORMAT =================
export function dtf1(dt, format = "HH[h]mm") {
  if (!dt) return null
  return m(dt).format(format)
}

export function dtf2(dt, displayTime = false) {
  if (!dt) return null

  const timeObj = m(dt)
  const nowObj = now()

  const date = timeObj.format("DD/MM/YYYY")
  const time = displayTime ? `[${timeObj.format("HH[h]mm[']")}] ` : ""

  const homnay = nowObj.format("DD/MM/YYYY")
  const ngaymai = nowObj.clone().add(1, "d").format("DD/MM/YYYY")
  const ngaykia = nowObj.clone().add(2, "d").format("DD/MM/YYYY")
  const homqua = nowObj.clone().subtract(1, "d").format("DD/MM/YYYY")
  const homkia = nowObj.clone().subtract(2, "d").format("DD/MM/YYYY")

  const tuannay = nowObj.format("WW")
  const namnay = nowObj.format("YYYY")

  if (date === homnay) return timeObj.format(`${time}[Hôm nay (]DD/MM[)]`)
  if (date === ngaymai) return timeObj.format(`${time}[Ngày mai (]DD/MM[)]`)
  if (date === ngaykia) return timeObj.format(`${time}[Ngày kia (]DD/MM[)]`)
  if (date === homqua) return timeObj.format(`${time}[Hôm qua (]DD/MM[)]`)
  if (date === homkia) return timeObj.format(`${time}[Hôm kia (]DD/MM[)]`)

  if (timeObj.format("YYYY") === namnay) {
    if (
      timeObj.format("WW") === tuannay &&
      ![homnay, ngaymai, ngaykia].includes(date)
    ) {
      return timeObj.format(
        `${time}[${getDayNameByDate(dt)}] [Tuần này (]DD/MM[)]`
      )
    }

    if (+timeObj.format("WW") === +tuannay + 1) {
      return timeObj.format(
        `${time}[${getDayNameByDate(dt)}] [ Tuần sau (]DD/MM[)]`
      )
    }

    return timeObj.format(`${time}[${getDayNameByDate(dt)}] [ (]DD/MM[)]`)
  }

  return timeObj.format(`${time}[${getDayNameByDate(dt)}] [ (]DD/MM/YYYY[)]`)
}

export function dtf3(dt) {
  return dtf2(dt, true)
}

export function dtf4(dt) {
  if (!dt) return null

  const today = now().startOf("day")
  const target = m(dt).startOf("day")

  const diffDay = today.diff(target, "days")

  if (diffDay === 0) {
    return `${Math.abs(now().diff(m(dt), "minutes"))} phút`
  }

  return `${Math.abs(diffDay)} ngày`
}

export function dtf5Prefix(num) {
  return num < 0 ? "Còn lại " : "Quá hạn "
}

export function dtf6Suffix(num) {
  return num > 0 ? " trước" : " sau"
}

// ⚠️ giữ nguyên dtf5, dtf6, dtf7 logic (KHÔNG đụng sâu vì dễ vỡ)
// chỉ thay moment() -> m()

export function dtf5(dt) {
  if (!dt) return null

  const curDt = m([
    now().get("year"),
    now().get("month"),
    now().get("date"),
    now().get("hour"),
    now().get("minute"),
  ])

  const inputDt = m([
    m(dt).get("year"),
    m(dt).get("month"),
    m(dt).get("date"),
    m(dt).get("hour"),
    m(dt).get("minute"),
  ])

  const diffMonth = curDt.diff(inputDt, "months")
  const diffWeek = curDt.diff(inputDt, "weeks")
  const diffDay = curDt.diff(inputDt, "days")
  const diffHour = curDt.diff(inputDt, "hours")
  const diffMin = curDt.diff(inputDt, "minutes")

  if (Math.abs(diffMin) < 60)
    return `${dtf5Prefix(diffMin)}${Math.abs(diffMin)} phút`

  if (Math.abs(diffHour) < 24)
    return `${dtf5Prefix(diffHour)}${Math.abs(diffHour)} giờ ${Math.abs(
      diffMin - diffHour * 60
    )} phút`

  // giữ nguyên phần còn lại...
  return `${dtf5Prefix(diffDay)}${Math.abs(diffDay)} ngày`
}

// ================= SIMPLE =================
export function dtf8(dt) {
  if (!dt) return null

  const timeObj = m(dt)
  const time = `[${timeObj.format("HH[h]mm[']")}] `
  const currentYear = now().format("YYYY")

  if (timeObj.format("YYYY") === currentYear) {
    return timeObj.format(`${time}DD/MM`)
  }

  return timeObj.format(`${time}DD/MM/YYYY`)
}

export function durationDate(startDate, endDate) {
  if (!startDate || !endDate) return null

  const thisYear = now().format("YYYY")

  if (
    m(startDate).format("YYYY") === thisYear &&
    m(endDate).format("YYYY") === thisYear
  ) {
    return `${formatDate(startDate, "DD/MM")} - ${formatDate(endDate, "DD/MM")}`
  }

  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}